/**
 * Two-step verification helpers (#333) shared by the login challenge
 * (`MfaChallenge`) and Settings → Security (`SecuritySection`).
 *
 * The second factor itself is Firebase's (Identity Platform multi-factor):
 * authenticator app (TOTP) only — no SMS (removed, PO 2026-10-28) and no email
 * codes; the 10 recovery codes are the backup. The backend only keeps the recovery codes and a record of what is enrolled
 * (`POST /auth/mfa/sync`), and refuses single-factor tokens on 2FA accounts
 * (`401 mfa_required`, see utils/authEvents).
 */
import { standardErrorMessage } from './userError';

export const TOTP_FACTOR_ID = 'totp';
// Only to recognise a legacy phone factor in the enrolled list (SMS was removed;
// none exist in practice) — there is no way to add one.
export const PHONE_FACTOR_ID = 'phone';

/** Keep only digits, at most six — the code inputs' onChange filter. */
export const sanitizeCode = (value) => String(value || '').replace(/\D/g, '').slice(0, 6);

/** The hint/factor to preselect: the authenticator app when there is one. */
export const defaultHint = (hints) => {
    const list = Array.isArray(hints) ? hints : [];
    return list.find((h) => h?.factorId === TOTP_FACTOR_ID) || list[0] || null;
};

/** "Authenticator app" for a hint or enrolled factor (a legacy phone one says so). */
export const factorLabel = (factor, t) => {
    if (factor?.factorId === PHONE_FACTOR_ID) {
        return t('mfaFactorLegacyPhone', 'Phone (no longer supported)');
    }
    return t('mfaFactorTotp', 'Authenticator app');
};

/**
 * Display text for a Firebase multi-factor error. Only the codes a person can
 * act on get their own sentence; anything else is the standard one (#439).
 */
export const mfaErrorMessage = (err, t) => {
    switch (err?.code) {
        case 'auth/invalid-verification-code':
            return t('mfaErrInvalidCode', 'That code is not correct. Check it and try again.');
        case 'auth/totp-challenge-timeout':
        case 'auth/code-expired':
            return t('mfaErrExpired', 'That code has expired. Wait for a new one and try again.');
        case 'auth/too-many-requests':
            return t('mfaErrTooMany', 'Too many attempts. Please wait a few minutes and try again.');
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
            return t('mfaErrWrongPassword', 'Incorrect password.');
        default:
            return standardErrorMessage(t);
    }
};

/** The recovery codes as a .txt the user keeps. */
export const recoveryCodesText = (codes, t) => [
    t('mfaCodesFileTitle', 'PAI recovery codes'),
    t('mfaCodesFileNote', 'Each code works once. Keep them somewhere safe.'),
    '',
    ...(codes || []),
    '',
].join('\n');

export const downloadRecoveryCodes = (codes, t) => {
    const blob = new Blob([recoveryCodesText(codes, t)], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pai-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
};
